import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import PuModal from './PuModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Pus {
  pu_id: number;
  pu_name: string;
  pu_status: number;
  pu_image:string;
}

const ProductUses: React.FC = () => {
  const [Pus, setPus] = useState<Pus[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Pus | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchPu = async () => {
    try {
      const data = await apiRequest<Pus[]>({
        method: 'get',
        url: 'product-uses',
      });
      setPus(data);
    } catch (error) {
      showError('Failed to fetch Product Uses');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPu();
  }, []);

  const handleEditClick = (record: Pus) => {
    setIsEditMode(true);
    setInitialValues({
      pu_id: record.pu_id,
      pu_name: record.pu_name,
      pu_status: record.pu_status,
      pu_image: record.pu_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchPu();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (pu_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-product-uses/${pu_id}`,
      });
      showSuccess('Product Uses deleted');
      fetchPu();
    } catch {
      showError('Failed to delete Product Uses');
    }
  };

  const handleStatusToggle = async (PuId: number) => {
    setPus((prev) =>
      prev.map((item) =>
        item.pu_id === PuId
          ? { ...item, pu_status: item.pu_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-product-uses/${PuId}`,
      });
      showSuccess('Product Uses status updated');
    } catch (error) {
      showError('Failed to update status');
      setPus((prev) =>
        prev.map((item) =>
          item.pu_id === PuId
            ? { ...item, pu_status: item.pu_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Pus>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Pus> = [
    {
      title: 'Image',
      dataIndex: 'pu_image',
      key: 'pu_image',
      width: 500,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.pu_image}
            alt="Product Use"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),

    },
    {
      title: 'Product Uses',
      dataIndex: 'pu_name',
      key: 'pu_name',
      filters: Array.from(new Set(Pus.map((s) => s.pu_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.pu_name || null,
      onFilter: (value, record) => record.pu_name.includes(value as string),
      sorter: (a, b) => a.pu_name.localeCompare(b.pu_name),
      sortOrder: sortedInfo.columnKey === 'pu_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.pu_name}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Pus) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.pu_status}
                onChange={() => handleStatusToggle(record.pu_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this Product use?"
              onConfirm={() => handleDelete(record.pu_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Product Uses</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Product Uses
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Pus>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="pu_id"
          dataSource={Pus.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No Product Uses found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, Pus.length)} of{' '}
                {Pus.length}
              </span>
              <Pagination
                {...pagination}
                total={Pus.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <PuModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default ProductUses;